import React, { useState, useEffect } from 'react';

// Discount Tiers Manager for volume-based discounts (based on total sheets printed)
const DiscountTiersManager = () => {
  // State for volume discount tiers
  const [volumeDiscountTiers, setVolumeDiscountTiers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // API URL
  const API_URL = process.env.REACT_APP_API_URL || '';
  
// Load discount tiers from API
useEffect(() => {
  const fetchDiscountTiers = async () => {
    console.log("DiscountTiersManager: useEffect triggered."); // <-- ADD LOG
    setIsLoading(true);
    setError(null); // Clear previous errors
    setSuccessMessage(null); // Clear previous success

    const apiUrl = `${API_URL}/api/discount-tiers`; // Construct URL first
    console.log("DiscountTiersManager: Attempting to fetch from:", apiUrl); // <-- ADD LOG (Check API_URL)

    try {
      const response = await fetch(apiUrl, { // Use the constructed URL
        credentials: 'include' // Important for auth cookies
      });

      console.log("DiscountTiersManager: Fetch response received, status:", response.status); // <-- ADD LOG

      if (!response.ok) {
        // Try to get error message from response body if possible
        let errorBody = null;
        try {
            errorBody = await response.json();
        } catch (parseError) {
            // Ignore if response body is not JSON
        }
        console.error("DiscountTiersManager: Fetch failed!", response.status, errorBody); // <-- ADD LOG
        throw new Error(`Failed to fetch discount tiers: ${response.status}${errorBody ? ` - ${errorBody.error || JSON.stringify(errorBody)}` : ''}`);
      }

      const data = await response.json();
      console.log("DiscountTiersManager: Fetch successful, data received:", data); // <-- ADD LOG

      // Check if the expected data structure is present
      if (!data || !Array.isArray(data.volume_discount_tiers)) {
          console.error("DiscountTiersManager: Invalid data structure received:", data); // <-- ADD LOG
          throw new Error("Invalid data structure received from API.");
      }

      // Sort tiers by min_sheets (descending)
      setVolumeDiscountTiers(data.volume_discount_tiers.sort((a, b) => b.min_sheets - a.min_sheets));

      // setError(null); // Already cleared at the start
    } catch (err) {
      console.error('DiscountTiersManager: Error in fetchDiscountTiers:', err); // <-- MODIFY LOG
      setError(`Failed to load discount tiers: ${err.message}`);
    } finally {
      console.log("DiscountTiersManager: Setting isLoading to false."); // <-- ADD LOG
      setIsLoading(false);
    }
  };

  fetchDiscountTiers();
}, [API_URL]); // Dependency array remains the same
  
  // Add new volume discount tier
  const addVolumeTier = () => {
    // Find lowest min_sheets or use 0 if there are no tiers
    const lowestMinSheets = volumeDiscountTiers.length > 0 
      ? Math.min(...volumeDiscountTiers.map(tier => tier.min_sheets)) 
      : 0;
    
    // Default min_sheets is one more than the lowest, or 50 if the lowest is 0
    const defaultMinSheets = lowestMinSheets === 0 ? 50 : lowestMinSheets + 50;
    
    // Add new tier
    setVolumeDiscountTiers([
      ...volumeDiscountTiers,
      {
        min_sheets: defaultMinSheets,
        discount_factor: 0.95, // 5% discount by default
        discount_percent: 5
      }
    ]);
  };
  
  // Remove volume discount tier
  const removeVolumeTier = (index) => {
    const newTiers = [...volumeDiscountTiers];
    newTiers.splice(index, 1);
    setVolumeDiscountTiers(newTiers);
  };
  
  // Update volume discount tier
  const updateVolumeTier = (index, field, value) => {
    const newTiers = [...volumeDiscountTiers];
    
    if (field === 'min_sheets') {
      newTiers[index].min_sheets = parseInt(value) || 0;
    } else if (field === 'discount_percent') {
      // Ensure discount percent is between 0 and 100
      const percent = Math.min(100, Math.max(0, parseFloat(value) || 0));
      newTiers[index].discount_percent = percent;
      newTiers[index].discount_factor = (100 - percent) / 100;
    }
    
    setVolumeDiscountTiers(newTiers);
  };
  
  // Save changes
  const saveChanges = async () => {
    try {
      // Ensure there's a tier with min_sheets = 0 for default
      let volumeTiers = [...volumeDiscountTiers];
      
      // Add default tier if not present
      if (!volumeTiers.some(tier => tier.min_sheets === 0)) {
        volumeTiers.push({
          min_sheets: 0,
          discount_factor: 1,
          discount_percent: 0
        });
      }
      
      // Sort tiers by min_sheets (descending)
      volumeTiers.sort((a, b) => b.min_sheets - a.min_sheets);
      
      // Send to API
      const response = await fetch(`${API_URL}/api/discount-tiers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Important for auth cookies
        body: JSON.stringify({
          volume_discount_tiers: volumeTiers
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save discount tiers: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update state with returned data
      setVolumeDiscountTiers(data.volume_discount_tiers);
      
      // Show success message
      setSuccessMessage('Discount tiers saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      
      setError(null);
    } catch (err) {
      console.error('Error saving discount tiers:', err);
      setError(`Failed to save discount tiers: ${err.message}`);
    }
  };
  
  if (isLoading) {
    return <div className="p-4 text-center">Loading discount tiers...</div>;
  }
  
  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Volume Discount Manager</h2>
      
      {error && (
        <div className="p-4 mb-4 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="p-4 mb-4 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}
      
      <div className="mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Volume Discounts (Based on Total Sheets)</h3>
          <p className="text-gray-600 mb-4">
            Set discounts based on the total number of sheets needed for a job. Higher volumes get lower prices.
            These discounts apply to both material costs and print costs.
          </p>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Number of Sheets
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount (%)
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {volumeDiscountTiers.map((tier, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {tier.min_sheets === 0 ? (
                        <span className="text-gray-500">Default tier (0+)</span>
                      ) : (
                        <input
                          type="number"
                          value={tier.min_sheets}
                          onChange={(e) => updateVolumeTier(index, 'min_sheets', e.target.value)}
                          min="1"
                          className="w-24 p-1 border rounded"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        type="number"
                        value={tier.discount_percent}
                        onChange={(e) => updateVolumeTier(index, 'discount_percent', e.target.value)}
                        min="0"
                        max="100"
                        step="0.1"
                        disabled={tier.min_sheets === 0}
                        className={`w-20 p-1 border rounded ${tier.min_sheets === 0 ? 'bg-gray-100' : ''}`}
                      />
                      <span className="ml-1">%</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {tier.min_sheets !== 0 && (
                        <button
                          onClick={() => removeVolumeTier(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4">
            <button
              onClick={addVolumeTier}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Add Tier
            </button>
          </div>
        </div>
      </div>
      
      <div className="flex justify-end mb-8">
        <button
          onClick={saveChanges}
          className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          Save Changes
        </button>
      </div>
      
      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-8">
        <h3 className="text-lg font-semibold mb-2">How Volume Discounts Work</h3>
        <p className="mb-4">
          Volume discounts automatically reduce prices for larger print jobs based on the total number of sheets needed for production.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-2">How Sheets Are Counted:</h4>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Each SRA3 (or standard size) sheet counts as one unit</li>
              <li>Total is calculated based on imposition/nesting</li>
              <li>For booklets, calculation accounts for signatures/impositions</li>
              <li>Sheet count directly reflects your actual press run length</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium mb-2">What Gets Discounted:</h4>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Material costs (paper, vinyl, board, etc.)</li> 
              <li>Print/click costs</li>
              <li>Finishing costs remain the same (no discount)</li>
              <li>Discounts are applied before markup</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-blue-100 rounded">
          <h4 className="font-medium mb-2">Examples:</h4>
          <p className="text-sm mb-2">
            <strong>Small Format:</strong> 1,000 A4 flyers (double-sided) might require 500 SRA3 sheets 
            with 2-up imposition. With a 15% discount at 500+ sheets, both paper and click costs would be reduced.
          </p>
          <p className="text-sm">

          </p>
        </div>
      </div>
    </div>
  );
};

export default DiscountTiersManager;